import clsx from 'clsx';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  AdditionalFaqList,
  KnowledgeBaseItem,
  KnowledgeBaseSectionKey,
  KnowledgeBaseView,
} from '../../../types/knowledgeQuestions';
import {
  Block,
  BlockSkeleton,
  Button,
  IconArrowRight,
  IconChevronRight,
  IconMessage,
  IconPlus,
  QueryGate,
  Reason,
  Row,
  SectionBody,
  SectionHead,
  StatusLabel,
  When,
} from '../../../ui/v2';
import {
  useAdditionalFaqs,
  useKnowledgeBaseView,
  useOpenKnowledgeQuestions,
} from '../../KnowledgeQuestions/knowledgeData';
import {
  ADDITIONAL_FAQS_HREF,
  KNOWLEDGE_QUESTIONS_BASE,
  sortFaqs,
} from '../../KnowledgeQuestions/knowledgeModel';
import { FaqState } from './AdditionalFaqsSection';
import {
  type Fact,
  parseHours,
  parseStoreInfo,
  type StoreInfoFacts,
} from './knowledgeFacts';

/** Longer than this starts collapsed. */
const COLLAPSE_LINES = 24;

/** Questions shown on the FAQs card; the rest are one click away. */
const FAQ_PREVIEW = 4;

const linkClass = 'text-v2-accent hover:text-v2-accent-hover';

/** Section headings. The API sends the content; the page owns the labels. */
const SECTION_COPY: Record<
  KnowledgeBaseSectionKey,
  { title: string; description: string }
> = {
  menu: {
    title: 'Menu',
    description: 'The menu your agent reads from.',
  },
  additional_faqs: {
    title: 'Additional FAQs',
    description: "Answers you've published for your agent.",
  },
  store_info: {
    title: 'Store info',
    description: 'What your agent looks up on calls.',
  },
  hours: {
    title: 'Hours',
    description: 'What your agent looks up on calls.',
  },
  special_hours: {
    title: 'Special hours',
    description: 'What your agent looks up on calls.',
  },
  deals: {
    title: 'Deals',
    description: 'What your agent looks up on calls.',
  },
  documents: {
    title: 'Files',
    description: 'Documents stored for your agent.',
  },
  websites: {
    title: 'Websites',
    description: 'Pages stored for your agent.',
  },
  notes: {
    title: 'Notes',
    description: 'Text stored for your agent.',
  },
};

const LINK_SECTIONS = new Set<KnowledgeBaseSectionKey>([
  'documents',
  'websites',
]);

/**
 * What Arthur has for this store. Questions waiting on an answer sit on top,
 * then the store's Additional FAQs (the part the store edits), then a
 * read-only copy of what Arthur uses, in order: the menu name (the
 * ordering markdown stays internal), the store info the agent looks up
 * on calls, then files, websites, and notes stored on the knowledge base.
 * Empty sections stay hidden.
 */
const KnowledgeBaseSection: React.FC = () => {
  const view = useKnowledgeBaseView();
  return (
    <SectionBody measure="events">
      <SectionHead
        title="Knowledge base"
        description={
          <>
            What your agent knows about your store. To change what your agent
            says, add or edit an{' '}
            <Link to={ADDITIONAL_FAQS_HREF} className={linkClass}>
              FAQ
            </Link>
            .
          </>
        }
      />
      <QuestionsBanner />
      <FaqsCard />
      <div className="mt-8">
        <QueryGate
          query={view}
          errorTitle="What your agent knows could not be loaded."
          skeleton={<BlockSkeleton rows={6} />}
        >
          {(data: KnowledgeBaseView) => {
            const sections = data.sections.filter(
              (section) =>
                // The FAQs card above stands in for the published FAQ file.
                section.key !== 'additional_faqs' &&
                SECTION_COPY[section.key] &&
                section.items.some(
                  (item) => item.content.trim() || item.url || item.title,
                ),
            );
            if (sections.length === 0) {
              return <Reason>Nothing here yet.</Reason>;
            }
            return (
              <>
                {sections.map((section, i) => (
                  <Block
                    key={section.key}
                    first={i === 0}
                    title={SECTION_COPY[section.key].title}
                    description={SECTION_COPY[section.key].description}
                  >
                    {LINK_SECTIONS.has(section.key) ? (
                      <SourceLinks items={section.items} />
                    ) : (
                      <div className="flex flex-col gap-5">
                        {section.items.map((item, j) => (
                          <KnowledgeItem
                            key={`${section.key}:${j}`}
                            sectionKey={section.key}
                            item={item}
                            showTitle={
                              section.items.length > 1 ||
                              section.key === 'menu' ||
                              section.key === 'notes'
                            }
                          />
                        ))}
                      </div>
                    )}
                  </Block>
                ))}
              </>
            );
          }}
        </QueryGate>
      </div>
    </SectionBody>
  );
};

/**
 * Questions callers asked that the agent couldn't answer. A call to action,
 * not a list: it opens the first one, where the store answers (it becomes
 * an Additional FAQ) or dismisses it. Hidden while nothing is waiting.
 */
const QuestionsBanner: React.FC = () => {
  const navigate = useNavigate();
  const list = useOpenKnowledgeQuestions();
  const count = list.data?.count ?? 0;
  const first = list.data?.results[0];
  if (!first || count === 0) return null;
  const more = count - 1;
  return (
    <section
      aria-label="Questions to answer"
      className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-v2 border border-v2-accent/25 bg-v2-accent/5 px-4 py-3"
    >
      <span className="shrink-0 text-v2-accent">
        <IconMessage size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-v2-13 font-semibold text-v2-fg">
          {count === 1
            ? '1 question to answer'
            : `${count} questions to answer`}
        </p>
        <p className="mt-0.5 truncate text-v2-12 text-v2-fg-3">
          Callers asked “{first.question}”{more > 0 && ` and ${more} more`}.
          Answer to add an FAQ, or dismiss.
        </p>
      </div>
      <Button
        size="sm"
        variant="primary"
        trailing={<IconArrowRight size={14} />}
        onClick={() => navigate(`${KNOWLEDGE_QUESTIONS_BASE}/${first.id}`)}
      >
        Answer questions
      </Button>
    </section>
  );
};

/**
 * The store's Additional FAQs at a glance: the first few questions only.
 * The card opens the full list with answers, where they're added, edited
 * and published.
 */
const FaqsCard: React.FC = () => {
  const navigate = useNavigate();
  const faqs = useAdditionalFaqs();
  const openAll = () => navigate(ADDITIONAL_FAQS_HREF);
  return (
    <section
      aria-label="Additional FAQs"
      className="mt-6 max-w-[80ch] rounded-v2 border border-v2-line"
    >
      <div className="flex items-start justify-between gap-4 px-4 pb-2 pt-3">
        <div className="min-w-0">
          <h3 className="text-v2-13 font-semibold text-v2-fg">
            Additional FAQs
            {faqs.data && faqs.data.results.length > 0 && (
              <span className="v2-num ml-1.5 font-normal text-v2-fg-3">
                {faqs.data.results.length}
              </span>
            )}
          </h3>
          <p className="mt-0.5 text-v2-12 text-v2-fg-3">
            Answers your agent gives beyond your menu and hours.
          </p>
        </div>
        {faqs.data && faqs.data.results.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            trailing={<IconChevronRight size={14} />}
            onClick={openAll}
            className="-mr-2"
          >
            View all
          </Button>
        )}
      </div>
      <div className="px-4 pb-3">
        <QueryGate
          query={faqs}
          errorTitle="Additional FAQs could not be loaded."
          skeleton={<BlockSkeleton rows={3} />}
        >
          {(data: AdditionalFaqList) =>
            data.results.length === 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-3 py-1">
                <Reason>No FAQs yet.</Reason>
                <Button
                  size="sm"
                  variant="primary"
                  leading={<IconPlus size={14} />}
                  onClick={() => navigate(`${ADDITIONAL_FAQS_HREF}?add=1`)}
                >
                  Add FAQ
                </Button>
              </div>
            ) : (
              <>
                <ul className="-mx-2">
                  {sortFaqs(data.results)
                    .slice(0, FAQ_PREVIEW)
                    .map((faq) => {
                      return (
                        <li key={faq.id}>
                          <button
                            type="button"
                            onClick={openAll}
                            className="flex w-full items-center gap-3 rounded-v2 px-2 py-2 text-left text-v2-13 text-v2-fg hover:bg-v2-sunken/70 focus-visible:bg-v2-sunken focus-visible:outline-none"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {faq.question}
                            </span>
                            <FaqState faq={faq} />
                            <IconChevronRight
                              size={14}
                              className="shrink-0 text-v2-fg-4"
                            />
                          </button>
                        </li>
                      );
                    })}
                </ul>
                {data.results.length > FAQ_PREVIEW && (
                  <button
                    type="button"
                    onClick={openAll}
                    className={`mt-1 text-v2-12 font-medium ${linkClass}`}
                  >
                    View {data.results.length - FAQ_PREVIEW} more
                  </button>
                )}
              </>
            )
          }
        </QueryGate>
      </div>
    </section>
  );
};

const SourceLinks: React.FC<{ items: KnowledgeBaseItem[] }> = ({ items }) => (
  <div className="flex max-w-[80ch] flex-col gap-2">
    {items.map((item, i) => {
      const label =
        item.title.replace(/^https?:\/\//, '') || item.url || 'Link';
      return item.url ? (
        <a
          key={`${item.url}:${i}`}
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className={`break-all ${linkClass}`}
        >
          {label}
        </a>
      ) : (
        <span
          key={`${label}:${i}`}
          className="break-words text-v2-13 text-v2-fg"
        >
          {label}
        </span>
      );
    })}
  </div>
);

const KnowledgeItem: React.FC<{
  sectionKey: KnowledgeBaseSectionKey;
  item: KnowledgeBaseItem;
  showTitle: boolean;
}> = ({ sectionKey, item, showTitle }) => (
  <div>
    {(showTitle || item.updated_at) && (
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 text-v2-12">
        {showTitle && (
          <span className="font-semibold text-v2-fg-2">{item.title}</span>
        )}
        {item.updated_at && (
          <span className="text-v2-fg-3">
            Updated <When value={item.updated_at} />
          </span>
        )}
      </div>
    )}
    {sectionKey !== 'menu' && item.content.trim() && (
      <KnowledgeContent sectionKey={sectionKey} content={item.content} />
    )}
  </div>
);

const KnowledgeContent: React.FC<{
  sectionKey: KnowledgeBaseSectionKey;
  content: string;
}> = ({ sectionKey, content }) => {
  if (sectionKey === 'store_info') {
    const facts = parseStoreInfo(content);
    if (facts) return <StoreInfoFactsView facts={facts} />;
  }
  if (sectionKey === 'hours' || sectionKey === 'special_hours') {
    const rows = parseHours(content);
    if (rows) return <HoursView rows={rows} />;
  }
  return <RawText content={content} />;
};

const factCard = 'max-w-[80ch] rounded-v2 border border-v2-line px-3 py-0.5';

const StoreInfoFactsView: React.FC<{ facts: StoreInfoFacts }> = ({ facts }) => (
  <div className="flex max-w-[80ch] flex-col gap-4">
    {facts.details.length > 0 && (
      <dl aria-label="Details" className={factCard}>
        {facts.details.map((fact) => (
          <Row key={fact.label} label={fact.label}>
            <FactValue fact={fact} />
          </Row>
        ))}
      </dl>
    )}
    {facts.services.length > 0 && (
      <dl
        aria-label="Services"
        className={clsx(factCard, 'grid gap-x-8 sm:grid-cols-2')}
      >
        {facts.services.map((service) => (
          <Row key={service.label} label={service.label}>
            <StatusLabel
              size="sm"
              tone={service.offered ? 'success' : 'neutral'}
              hollow={!service.offered}
            >
              {service.offered ? 'Yes' : 'No'}
            </StatusLabel>
          </Row>
        ))}
      </dl>
    )}
  </div>
);

const FactValue: React.FC<{ fact: Fact }> = ({ fact }) => {
  if (fact.label === 'Website' && /^https?:\/\//.test(fact.value)) {
    return (
      <a
        href={fact.value}
        target="_blank"
        rel="noreferrer"
        className="break-all text-v2-accent hover:text-v2-accent-hover"
      >
        {fact.value.replace(/^https?:\/\//, '')}
      </a>
    );
  }
  return (
    <span className="whitespace-pre-line break-words leading-5">
      {fact.value}
    </span>
  );
};

const HoursView: React.FC<{ rows: Fact[] }> = ({ rows }) => (
  <dl className={clsx(factCard, 'max-w-[56ch]')}>
    {rows.map((row, i) => (
      <Row key={`${row.label}:${i}`} label={row.label}>
        <span className="v2-num">
          {row.value.charAt(0).toUpperCase() + row.value.slice(1)}
        </span>
      </Row>
    ))}
  </dl>
);

const RawText: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n');
  const long = lines.length > COLLAPSE_LINES;
  const [expanded, setExpanded] = useState(false);
  const shown =
    long && !expanded ? lines.slice(0, COLLAPSE_LINES).join('\n') : content;
  return (
    <>
      <pre className="max-w-[80ch] whitespace-pre-wrap break-words rounded-v2 border border-v2-line bg-v2-sunken/60 px-3 py-2.5 font-[inherit] text-v2-12 leading-5 text-v2-fg-2">
        {shown}
      </pre>
      {long && (
        <Button
          size="sm"
          variant="ghost"
          className="mt-1"
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? 'Show less' : `Show all ${lines.length} lines`}
        </Button>
      )}
    </>
  );
};

export default KnowledgeBaseSection;
